import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { commandCenterApi, formatCurrencyWhole } from '../../utils/api';
import { toast } from '../../components/Toast';
import { monthLabelOf } from './businessMonth';

// The two editors behind the ⋮ on BE העסק.
//
// They exist because the money moved OFF the dashboard (owner, 01/09/2026).
// A row of live number inputs turns a reading screen into an editing screen:
// every glance at "how are we doing" happened one keystroke away from
// rewriting a price, and a mis-typed digit in a cell that saves on blur is
// silent. The board now reads, and writing is a deliberate two-step.
//
// The split between them is the same one the whole feature rests on:
//   • CustomerDetailsModal — the STANDING record. What they pay from now on,
//     who signed, where their folder is. Changing it never touches history.
//   • ChargeModal — ONE month. What that month was billed, whether it arrived
//     and when. Changing it never touches the standing price.
// Two modals rather than one form with two fieldsets, because saving "the
// contact person" and saving "March was paid on the 3rd" are not one action
// and must not share a submit button.

const MAX_NOTE = 500;
const MONTH_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

/**
 * PEAK's headcount, month by month — the denominator of הכנסות לעובד.
 *
 * A year at a time, twelve fields, because a headcount changes a few times a
 * year and the question when editing it is "who was here in March", which a
 * calendar answers. A field left EMPTY is the default (the placeholder says the
 * number) and saves as "not entered" — so raising the default later moves every
 * month nobody touched. Months before go-live are shown, hatched and locked,
 * so the year reads whole rather than starting at May for no visible reason.
 *
 * Switching year is blocked while there are unsaved edits: the save is per
 * year, and a tab that silently threw typed numbers away would be the worst
 * kind of form.
 */
export function HeadcountModal({ onSaved, onClose }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  // What the fields held when the year loaded, and what they hold now. The save
  // sends only the months that differ, so an untouched month is never
  // re-written — another admin's edit to it survives, and its updated_by stays
  // theirs.
  const [loaded, setLoaded] = useState({});
  const [values, setValues] = useState({});
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setLoadError(false);
    commandCenterApi.getCrmHeadcount(year)
      .then(({ data: d }) => {
        if (cancelled) return;
        const initial = Object.fromEntries(d.months.map(m => [m.month, m.is_default ? '' : String(m.employees)]));
        setData(d);
        setLoaded(initial);
        setValues(initial);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(true);
        toast(err.response?.data?.error || 'טעינת מספר העובדים נכשלה', 'error');
      });
    return () => { cancelled = true; };
  }, [year, reloadKey]);

  const changed = Object.keys(values).filter(m => values[m] !== loaded[m]);
  const dirty = changed.length > 0;

  const liveYear = Number((data?.live_from || '2026').slice(0, 4));
  const years = [];
  for (let y = Math.min(liveYear, thisYear); y <= thisYear + 1; y++) years.push(y);

  const invalid = data?.months.some((m) => {
    const v = values[m.month];
    return !m.prelaunch && v !== '' && !(Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 1000);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!data || invalid) return;
    setSaving(true);
    try {
      const months = Object.fromEntries(
        changed.map(m => [m, values[m] === '' ? null : Number(values[m])]),
      );
      await commandCenterApi.saveCrmHeadcount(months);
      toast('מספר העובדים נשמר');
      onSaved();
    } catch (err) {
      toast(err.response?.data?.error || 'שמירת מספר העובדים נכשלה', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} size={520} closeOnBackdrop={false} ariaLabel="מספר עובדים בחודש">
      <h2 className="modal-title">מספר עובדים בחודש</h2>
      <p className="cc-field-hint" style={{ marginTop: 0 }}>
        המכנה של <b>הכנסות לעובד</b>. שדה ריק נחשב {data?.default_employees ?? 2} עובדים.
      </p>
      <div className="cc-years" role="group" aria-label="שנה" style={{ marginBottom: 14, width: 'fit-content' }}>
        {years.map(y => (
          <button key={y} type="button"
            className={`cc-year${y === year ? ' is-on' : ''}`}
            aria-pressed={y === year}
            disabled={dirty && y !== year}
            title={dirty && y !== year ? 'שמור או בטל את השינויים קודם' : undefined}
            onClick={() => setYear(y)}
          >{y}</button>
        ))}
      </div>
      <form onSubmit={submit}>
        {loadError ? (
          <div className="cc-empty">
            טעינת מספר העובדים נכשלה.{' '}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setReloadKey(k => k + 1)}>
              נסה שוב
            </button>
          </div>
        ) : !data ? (
          <div className="cc-empty">טוען…</div>
        ) : (
          <div className="cc-headcount-grid">
            {data.months.map((m, i) => (
              <label key={m.month} className={`cc-headcount-cell${m.prelaunch ? ' is-prelaunch' : ''}`}>
                <span className="cc-headcount-month">{MONTH_SHORT[i]}</span>
                {m.prelaunch ? (
                  <span className="cc-headcount-pre" title="PEAK טרם עלתה לאוויר">טרם עלייה</span>
                ) : (
                  <input className="form-control" type="number" min="1" max="1000" step="1"
                    inputMode="numeric" aria-label={`עובדים ב${monthLabelOf(m.month)}`}
                    placeholder={String(data.default_employees)}
                    value={values[m.month]}
                    onChange={(e) => {
                      const v = e.target.value;
                      setValues(prev => ({ ...prev, [m.month]: v }));
                    }} />
                )}
              </label>
            ))}
          </div>
        )}
        {invalid && <div className="cc-field-error">מספר עובדים הוא מספר שלם בין 1 ל-1000.</div>}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success" disabled={saving || !data || invalid || !dirty}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** The standing record. Sends a PARTIAL patch — see `saveCrmCustomer`. */
export function CustomerDetailsModal({ customer, onSaved, onClose }) {
  // Empty, not "0", while no fee was ever entered: pre-filling 0 would save it
  // as a deliberate ₪0 (חינם) the first time someone edits only the contact.
  const [fee, setFee] = useState(
    customer.monthly_fee_entered === false ? '' : String(customer.monthly_fee ?? 0),
  );
  const [contact, setContact] = useState(customer.contact_name || '');
  const [folder, setFolder] = useState(customer.folder_url || '');
  const [started, setStarted] = useState(customer.started_month || '');
  const [ended, setEnded] = useState(customer.ended_month || '');
  const [note, setNote] = useState(customer.customer_note || '');
  const [saving, setSaving] = useState(false);

  // The system's own join month — the ISRAEL month created_at falls in — is
  // what `started` overrides. Taken from the server (`created_month`, the same
  // rule its arithmetic uses) rather than sliced from the UTC timestamp here,
  // which put a restaurant created in the first hours of a month in the one
  // before. Shown as the field's placeholder-ish default rather than
  // pre-filled, so an empty field visibly means "use the system date" and
  // typing a month visibly means "override it".
  const systemJoinMonth = customer.created_month || String(customer.created_at || '').slice(0, 7);

  // A customer cannot leave before they arrived, and the server refuses it —
  // said here so the refusal arrives while the month is being picked. Checked
  // against whichever start is CURRENTLY in the field, override or system.
  const effectiveStart = started || systemJoinMonth;
  const startedError = started && ended && started > ended
    ? `הלקוח סיים ב-${monthLabelOf(ended)} — חודש ההתחלה לא יכול להיות מאוחר יותר`
    : '';
  const endedError = ended && effectiveStart && ended < effectiveStart
    ? `הלקוח קיים במערכת רק מ-${monthLabelOf(effectiveStart)}`
    : '';

  // Mirrors server/lib/crm.js#normalizeFolderUrl so the refusal is immediate
  // and explains itself, rather than arriving as a toast after a round trip.
  // The SERVER is still the authority — this is a courtesy, not the guard.
  const folderError = (() => {
    const raw = folder.trim();
    if (!raw) return '';
    if (!/^https?:\/\//i.test(raw)) return 'הקישור חייב להתחיל ב-https:// (או http://)';
    try { new URL(raw); return ''; } catch { return 'כתובת לא תקינה'; }
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (folderError || endedError || startedError) return;
    const feeBlank = fee.trim() === '';
    // Blank means "leave it as not entered" — which only exists while it IS not
    // entered. Emptying a fee that has a value would otherwise save as a silent
    // no-op under a "saved" toast.
    if (feeBlank && customer.monthly_fee_entered !== false) {
      toast('יש להזין חיוב לחודש — 0 אם הלקוח לא משלם', 'error');
      return;
    }
    const next = Number(fee);
    if (!feeBlank && (!Number.isFinite(next) || next < 0)) {
      toast('סכום לא תקין', 'error');
      return;
    }
    setSaving(true);
    try {
      const { data } = await commandCenterApi.saveCrmCustomer(customer.id, {
        // A blank field is left out of the patch, so the fee keeps whatever it
        // was — including "never entered". Typing 0 is what marks it חינם.
        ...(feeBlank ? {} : { monthly_fee: next }),
        contact_name: contact,
        folder_url: folder.trim(),
        started_month: started,
        ended_month: ended,
        notes: note,
      });
      // Money that was recorded and is now outside the relationship leaves the
      // totals. Correct, but never silently — the toast names the amount, at
      // either end of the relationship.
      const orphaned = data?.customer?.orphaned_charges || 0;
      if (orphaned) {
        toast(
          `נשמר. ${orphaned} חיובים רשומים מחוץ לתקופת הלקוח כבר לא נספרים`
          + (data.customer.orphaned_paid ? ` — כולל ${formatCurrencyWhole(data.customer.orphaned_paid)} שסומנו כשולמו` : ''),
          'error',
        );
      } else {
        toast('פרטי הלקוח נשמרו');
      }
      onSaved();
    } catch (err) {
      toast(err.response?.data?.error || 'שמירת פרטי הלקוח נכשלה', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size={460}
      closeOnBackdrop={false}
      ariaLabel={customer.name}
    >
      <h2 className="modal-title">{customer.name}</h2>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>חיוב לחודש (₪)</label>
          <input className="form-control" type="number" min="0" step="1" inputMode="decimal"
            value={fee} onChange={e => setFee(e.target.value)} placeholder="לא הוזן" autoFocus />
          {/* The one thing about this field that is not obvious, said where
              it is being changed rather than in a doc nobody opens. */}
          <div className="cc-field-hint">
            חל מהחודשים שעדיין לא נרשם בהם חיוב. חודשים שכבר נרשמו שומרים על הסכום שנרשם בהם.
          </div>
        </div>
        <div className="form-group">
          <label>איש קשר (חתם על ההסכם)</label>
          <input className="form-control" value={contact} maxLength={200}
            onChange={e => setContact(e.target.value)} placeholder="שם מלא" />
        </div>
        <div className="form-group">
          <label>קישור לתיקיית הלקוח</label>
          <input className="form-control" type="url" dir="ltr" value={folder}
            onChange={e => setFolder(e.target.value)}
            placeholder="https://drive.google.com/…" />
          {folderError && <div className="cc-field-error">{folderError}</div>}
        </div>
        <div className="form-group">
          <label>חודש התחלה בפועל</label>
          <input className="form-control" type="month" value={started} max={ended || undefined}
            onChange={e => setStarted(e.target.value)} />
          {startedError
            ? <div className="cc-field-error">{startedError}</div>
            : (
              <div className="cc-field-hint">
                {started
                  ? `מ${monthLabelOf(started)} ואילך הלקוח נספר בכל מדד בעמוד. ברירת המחדל של המערכת הייתה ${monthLabelOf(systemJoinMonth)}.`
                  : `ריק = תאריך ההצטרפות במערכת, ${systemJoinMonth ? monthLabelOf(systemJoinMonth) : '—'}. שימושי כשהלקוח היה שלנו לפני שהמסעדה נוצרה במערכת, או רק אחרי.`}
              </div>
            )}
        </div>
        <div className="form-group">
          <label>חודש סיום (אם הלקוח עזב)</label>
          <input className="form-control" type="month" value={ended} min={effectiveStart || undefined}
            onChange={e => setEnded(e.target.value)} />
          {endedError
            ? <div className="cc-field-error">{endedError}</div>
            : (
              <div className="cc-field-hint">
                {ended
                  ? `מ${monthLabelOf(ended)} ואילך הלקוח לא ייספר באף מדד בעמוד — לא בכמות הלקוחות, לא בהכנסות ולא בגבייה. החודש עצמו עדיין נספר.`
                  : 'השאר ריק כל עוד הלקוח איתנו. ריקון השדה מחזיר אותו לכל החישובים.'}
              </div>
            )}
        </div>
        <div className="form-group">
          <label>הערה</label>
          <textarea className="form-control" rows={2} maxLength={MAX_NOTE}
            value={note} onChange={e => setNote(e.target.value)} />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success"
            disabled={saving || !!folderError || !!endedError || !!startedError}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * One month's charge: which month, what it was billed, whether it arrived and when.
 *
 * The MONTH IS A FIELD here, not the board's context (owner, 01/09/2026). The
 * editor opens on whatever month the board is showing, and you can move it —
 * which is the difference between "record this month's payments" and "someone
 * paid me for March, let me put it in".
 *
 * Changing the month RELOADS that month's charge. It has to: the fields were
 * filled from the month the editor opened on, and carrying September's amount
 * onto July is exactly the silent error a month picker invites. While that
 * fetch is in flight the form is disabled rather than showing the previous
 * month's numbers under the new month's label.
 */
export function ChargeModal({ customer, month: initialMonth, onSaved, onClose }) {
  const [month, setMonth] = useState(initialMonth);
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState(false);
  const [paidAt, setPaidAt] = useState('');
  const [note, setNote] = useState('');
  const [hasCharge, setHasCharge] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // The customer's first month. A charge before it is refused by the server
  // (it would count in the revenue trend and be missing from the board), so
  // the picker says so up front rather than letting you type it and fail.
  const firstMonth = customer.first_billable_month || '';
  const lastMonth = customer.ended_month || '';

  // Fetched on OPEN as well as on every month change. The customer list this
  // dialog is opened from is deliberately period-independent — it carries no
  // charge state at all — so there is nothing to prefill from, and a dialog
  // that guessed would be guessing about money.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    commandCenterApi.getCrmCharge(customer.id, month)
      .then(({ data }) => {
        if (cancelled) return;
        setAmount(String(data.amount ?? 0));
        setPaid(!!data.paid);
        setPaidAt(data.paid_at || '');
        setNote(data.note || '');
        setHasCharge(!!data.has_charge);
      })
      .catch(() => { if (!cancelled) toast('טעינת החיוב לחודש שנבחר נכשלה', 'error'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customer.id, month]);

  const tooEarly = !!firstMonth && month < firstMonth;
  const tooLate = !!lastMonth && month > lastMonth;

  const submit = async (e) => {
    e.preventDefault();
    if (tooEarly || tooLate) return;
    const next = Number(amount);
    if (!Number.isFinite(next) || next < 0) {
      toast('סכום לא תקין', 'error');
      return;
    }
    setSaving(true);
    try {
      await commandCenterApi.saveCrmCharge(customer.id, month, {
        amount: next,
        paid,
        // Empty means "you decide": the server stamps today when marking paid
        // and NULLs the field when un-marking, so the two can never disagree.
        paid_at: paid ? (paidAt || undefined) : undefined,
        note,
      });
      // Names the month, because it is no longer necessarily the one the board
      // behind this dialog is showing — "החיוב נשמר" over a table that did not
      // change would read as a failed save.
      toast(`החיוב ל${monthLabelOf(month)} נשמר`);
      onSaved(month);
    } catch (err) {
      toast(err.response?.data?.error || 'שמירת החיוב נכשלה', 'error');
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || loading;

  return (
    <Modal
      onClose={onClose}
      size={420}
      ariaLabel={customer.name}
    >
      <h2 className="modal-title">{customer.name}</h2>
      <form onSubmit={submit}>
        <div className="form-group">
          <label>חודש החיוב</label>
          <input className="form-control" type="month" value={month}
            min={firstMonth || undefined} max={lastMonth || undefined}
            onChange={e => e.target.value && setMonth(e.target.value)} />
          {tooEarly && (
            <div className="cc-field-error">
              הלקוח קיים במערכת רק מ-{monthLabelOf(firstMonth)} — לא ניתן לרשום חיוב מוקדם יותר.
            </div>
          )}
          {tooLate && (
            <div className="cc-field-error">
              הלקוח סיים ב{monthLabelOf(lastMonth)} — לא ניתן לרשום חיוב מאוחר יותר.
            </div>
          )}
        </div>
        <div className="form-group">
          <label>סכום לחודש (₪)</label>
          <input className="form-control" type="number" min="0" step="1" inputMode="decimal"
            value={amount} disabled={loading} onChange={e => setAmount(e.target.value)} />
          {!hasCharge && !loading && (
            <div className="cc-field-hint">
              עדיין לא נרשם חיוב ל{monthLabelOf(month)} — הסכום שמוצג הוא התשלום החודשי הנוכחי.
              שמירה כאן קובעת אותו לחודש הזה בלבד.
            </div>
          )}
          {loading && <div className="cc-field-hint">טוען את החיוב לחודש שנבחר…</div>}
        </div>
        <div className="form-group">
          <label className="cc-check">
            <input type="checkbox" checked={paid} disabled={loading}
              onChange={e => setPaid(e.target.checked)} />
            התקבל תשלום
          </label>
        </div>
        {/* Only when paid: a date on an unpaid month is a contradiction, and
            the server clears it anyway. */}
        {paid && (
          <div className="form-group">
            <label>תאריך התשלום</label>
            <input className="form-control" type="date" value={paidAt} disabled={loading}
              onChange={e => setPaidAt(e.target.value)} />
            <div className="cc-field-hint">מתי הכסף באמת נכנס. ריק = היום.</div>
          </div>
        )}
        <div className="form-group">
          <label>הערה</label>
          <textarea className="form-control" rows={2} maxLength={MAX_NOTE}
            value={note} disabled={loading} onChange={e => setNote(e.target.value)}
            placeholder="אמצעי תשלום, מספר אסמכתא…" />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
          <button type="submit" className="btn btn-success" disabled={busy || tooEarly || tooLate}>
            {saving ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
